import _ from 'lodash';
import produce from 'immer';
import { moment } from 'i18n';
import { UPDATE_EXCHANGE_DATA } from 'redux/actions/markets';
import { getTradeHistory } from 'redux/selectors/exchange';
import { getSingleTradingPairSettings } from 'redux/selectors/settings';

export const EXCHANGE_ACTIONS = {
  CLEAR_EXCHANGE: 'CLEAR_EXCHANGE',
  UPDATE_DEPTH_CHART: 'UPDATE_DEPTH_CHART',
  UPDATE_TRADING_PAIR: 'UPDATE_TRADING_PAIR',
  UPDATE_ORDER_BOOK: 'UPDATE_ORDER_BOOK',
  UPDATE_ORDER_BOOK_ASKS: 'UPDATE_ORDER_BOOK_ASKS',
  UPDATE_ORDER_BOOK_BIDS: 'UPDATE_ORDER_BOOK_BIDS',
  UPDATE_TRADE_HISTORY: 'UPDATE_TRADE_HISTORY',
  UPDATE_ORDER_BOOK_SELECTION: 'UPDATE_ORDER_BOOK_SELECTION',
  UPDATE_ORDER_BOOK_TOTAL: 'UPDATE_ORDER_BOOK_TOTAL',
};

// The RT WebSocket event pushes trades in the backend's PascalCase shape:
// { OrderID, CurrencyType, OrderConfirmDate, MarketType, Volume, Rate,
//   ExecutionType, tradeId }. Keep the snake_case aliases as fallbacks so
// we don't regress if a different endpoint ever returns the old format.
const formatTradeHistory = (trade = {}) => {
  const {
    OrderID,
    OrderConfirmDate,
    Volume,
    Rate,
    ExecutionType,
    tradeId,
    // legacy / fallback shape
    execution_side,
    timestamp,
    volume,
    rate,
    id,
  } = trade;

  const ts = OrderConfirmDate || timestamp;
  return {
    time: ts
      ? moment
          .utc(ts)
          .local()
          .format('HH:mm:ss')
      : '',
    side: ExecutionType || execution_side,
    price: Rate !== undefined ? Rate : rate,
    volume: Volume !== undefined ? Volume : volume,
    id: tradeId || OrderID || id,
  };
};

const formatOrderBookItem = ([price, volume]) => ({ price, volume });

const formatOrderBook = (orderBook) => _.orderBy(orderBook.map(formatOrderBookItem), 'price', 'desc');

const getUpdatedOrderBook = (orderBook, singleItem, action) => {
  const index = _.findIndex(orderBook, ['price', singleItem.price]);

  if (action === 'Insert') {
    orderBook.push(singleItem);
  } else if (index >= 0) {
    if (action === 'Update') {
      orderBook[index] = singleItem;
    } else if (action === 'Delete') {
      orderBook.splice(index, 1);
    }
  } else if (singleItem.volume) {
    orderBook.push(singleItem);
  }

  return _.orderBy(orderBook, 'price', 'desc');
};

export const initializeOrderBook = (orderBook, dispatchType) => {
  const formattedOrderBook = formatOrderBook(orderBook);

  return (dispatch) => {
    const maxVolumeItem = _.maxBy(formattedOrderBook, 'volume') || {};
    const maxVolume = maxVolumeItem.volume;

    dispatch({
      type: dispatchType,
      payload: { orderBook: formattedOrderBook, maxVolume },
    });

    dispatch(updateDepthChart());
  };
};

export const updateOrderBook = (orderBookItem, dispatchType) => {
  const formattedOrderBookItem = formatOrderBookItem(orderBookItem.Data);

  return (dispatch, getState) => {
    const orderBook =
      dispatchType === EXCHANGE_ACTIONS.UPDATE_ORDER_BOOK_ASKS
        ? getState().exchange.orderBookAsk.slice(0)
        : getState().exchange.orderBookBid.slice(0);

    const updatedOrderBook = getUpdatedOrderBook(orderBook, formattedOrderBookItem, orderBookItem.TriggerType);

    dispatch({
      type: dispatchType,
      payload: updatedOrderBook,
    });

    dispatch(updateDepthChart());
  };
};

export const updateOrderBookTotal = ({ total_buys_base, total_buys_quote, total_sells_base, total_sells_quote }) => ({
  type: EXCHANGE_ACTIONS.UPDATE_ORDER_BOOK_TOTAL,
  payload: {
    bidBaseCurrency: total_buys_base,
    bidQuoteCurrency: total_buys_quote,
    askBaseCurrency: total_sells_base,
    askQuoteCurrency: total_sells_quote,
  },
});

export const initializeTradeHistory = (tradeHistory) => ({
  type: EXCHANGE_ACTIONS.UPDATE_TRADE_HISTORY,
  payload: tradeHistory.map(formatTradeHistory),
});

export const updateTradeHistory = (tradeHistoryItem) => {
  const formattedTradeHistory = formatTradeHistory(tradeHistoryItem.Data);

  return (dispatch, getState) => {
    const tradeHistory = getTradeHistory(getState());
    const updatedTradeHistory = produce(tradeHistory, (draft) => {
      draft.unshift(formattedTradeHistory);
      if (draft.length > 50) draft.length = 50;
    });

    dispatch({
      type: EXCHANGE_ACTIONS.UPDATE_TRADE_HISTORY,
      payload: updatedTradeHistory,
    });
  };
};

const getDepthChartData = (orderBook, type) => {
  const newOrderBook = orderBook.map((item) => ({
    price: item.price,
    volume: item.volume,
    [`${type}TotalVolume`]: item.volume,
  }));

  const sortedOrderBook = _.orderBy(newOrderBook, 'price', type === 'ask' ? 'asc' : 'desc');

  sortedOrderBook.forEach((item, i) => {
    if (i > 0) {
      item[`${type}TotalVolume`] += sortedOrderBook[i - 1][`${type}TotalVolume`];
    }
  });

  return sortedOrderBook;
};

export const updateDepthChart = () => {
  return (dispatch, getState) => {
    const {
      exchange: { orderBookAsk, orderBookBid },
    } = getState();

    const depthChartAsk = getDepthChartData(orderBookAsk, 'ask');
    const depthChartBid = getDepthChartData(orderBookBid, 'bid');

    dispatch({
      type: EXCHANGE_ACTIONS.UPDATE_DEPTH_CHART,
      payload: _.sortBy([...depthChartBid, ...depthChartAsk], 'price'),
    });
  };
};

export const updateTradingPair = (tradingPair) => {
  return (dispatch, getState) => {
    const {
      markets: { prices },
    } = getState();

    if (tradingPair === 'ALL') {
      dispatch({
        type: EXCHANGE_ACTIONS.UPDATE_TRADING_PAIR,
        payload: { tradingPair },
      });
      return;
    }

    // Markets may not be loaded yet — TradeData mounts from URL params
    // before the MK WebSocket event arrives. In that case we still want to
    // update the exchange trading pair so downstream components can set up,
    // and we skip the UPDATE_EXCHANGE_DATA / order-book-selection dispatches
    // until price data actually exists.
    const currentData = _.get(prices, [tradingPair.quoteCurrency, tradingPair.baseCurrency]);

    const tradingPairSettings = getSingleTradingPairSettings(getState(), {
      tradingPair,
    });

    if (currentData) {
      dispatch({
        type: UPDATE_EXCHANGE_DATA,
        payload: currentData,
      });
    }

    dispatch({
      type: EXCHANGE_ACTIONS.UPDATE_TRADING_PAIR,
      payload: { tradingPair, tradingPairSettings },
    });

    if (currentData) {
      dispatch(updateOrderBookSelection({ rowData: { price: currentData.lastPrice } }));
    }
  };
};

export const clearExchange = () => ({
  type: EXCHANGE_ACTIONS.CLEAR_EXCHANGE,
});

export const updateOrderBookSelection = ({ rowData: { price = 0 }, side, index }) => {
  if (!side) {
    return {
      type: EXCHANGE_ACTIONS.UPDATE_ORDER_BOOK_SELECTION,
      payload: { rate: price.toString() },
    };
  }

  return (dispatch, getState) => {
    const { exchange } = getState();
    const orderBook = side === 'bid' ? exchange.orderBookBid : exchange.orderBookAsk.slice(0);

    index = side === 'bid' ? index : orderBook.length - (index + 1);

    const payload = orderBook.slice(0, index + 1).reduce(
      (acc, { price, volume }) => ({
        price,
        volume: acc.volume + volume,
      }),
      { price: 0, volume: 0 },
    );

    dispatch({
      type: EXCHANGE_ACTIONS.UPDATE_ORDER_BOOK_SELECTION,
      payload: {
        rate: payload.price.toString(),
        volume: payload.volume.toString(),
        stop: payload.price.toString(),
      },
    });
  };
};
